import {
  Links,
  Meta,
  Outlet,
  Scripts,
  ScrollRestoration,
  useLoaderData,
  useRouteLoaderData,
} from "react-router"
import type { LinksFunction, MetaFunction } from "react-router"

import tailwindStyles from "./tailwind.css?url"
import { ErrorBoundary as ErrorBoundaryComponent } from "~/components/Errors"
import { BoundaryStatusProvider } from "~/contexts/BoundaryStatusContext"
import { AuthProvider, useAuth } from "~/contexts/AuthContext"
import { useStatusToasts } from "./components/Boundaries/useStatusToasts"
import { initPosthog } from "~/client/posthog.client"
import { isPosthogProduction } from "~/server/posthog.server"
import { useEffect } from "react"
import { ToastContainer, Bounce } from "react-toastify/unstyled"
import "react-toastify/ReactToastify.css"
import { CommandPalette } from "./components/CommandPalette"

export const meta: MetaFunction = () => {
  return [
    { title: "Pine59 Console" },
    {
      name: "description",
      content: "Pine59 Console - Manage your data and settings",
    },
  ]
}

export const links: LinksFunction = () => [
  { rel: "stylesheet", href: tailwindStyles },
  { rel: "preconnect", href: "https://fonts.googleapis.com" },
  {
    rel: "preconnect",
    href: "https://fonts.gstatic.com",
    crossOrigin: "anonymous",
  },
  {
    rel: "stylesheet",
    href: "https://fonts.googleapis.com/css2?family=Geist+Mono:wght@400;500;700&family=Instrument+Serif:ital@0;1&family=Radio+Canada+Big:ital,wght@0,400..700;1,400..700&display=swap",
  },
]

// Whether this deployment sends PostHog events (POSTHOG_ENV=production). The
// browser can't read the env, so the root loader carries it. Static for the
// life of the process, hence never revalidated.
export const loader = () => ({ posthogEnabled: isPosthogProduction() })

export const shouldRevalidate = () => false

// Hands the flag to posthog.client.ts before any component can call it; an
// inline script in <head> runs before hydration, so no effect ordering
// applies. Rendered only when enabled: an absent flag means "don't send".
function PosthogRuntime({ enabled }: { enabled: boolean | undefined }) {
  if (!enabled) return null
  return (
    <script
      dangerouslySetInnerHTML={{ __html: "window.__POSTHOG_ENABLED__=true" }}
    />
  )
}

function StatusToasts() {
  useStatusToasts()
  return null
}

function MaybeCommandPalette() {
  const { user, isLoading } = useAuth()
  // Only render for verified users - unverified users can't access protected routes
  if (isLoading || !user || !user.emailVerified) return null
  return <CommandPalette />
}

export default function App() {
  const { posthogEnabled } = useLoaderData<typeof loader>()
  useEffect(() => {
    initPosthog()
  }, [])
  return (
    <html lang="en" translate="no" suppressHydrationWarning={true}>
      <head>
        <meta charSet="utf-8" />
        {/*
          Disable in-browser translation (CAT-1090): tools like Google Translate
          mutate the DOM by wrapping text nodes in <font> elements, which makes
          React's removeChild/insertBefore throw on the next state change and
          crashes the app to the "Unexpected error" boundary. The console is
          authored in English; opt the whole page out of automatic translation.
        */}
        <meta name="google" content="notranslate" />
        <meta name="viewport" content="width=device-width, initial-scale=1" />
        <PosthogRuntime enabled={posthogEnabled} />
        <Favicons />
        <Meta />
        <Links />
      </head>
      <body suppressHydrationWarning={true}>
        <AuthProvider>
          <BoundaryStatusProvider>
            <StatusToasts />
            <MaybeCommandPalette />
            <Outlet />
          </BoundaryStatusProvider>
        </AuthProvider>
        <ToastContainer
          position="bottom-right"
          autoClose={5000}
          hideProgressBar={false}
          newestOnTop={false}
          closeOnClick={false}
          rtl={false}
          pauseOnFocusLoss
          draggable
          pauseOnHover
          theme="dark"
          transition={Bounce}
        />
        <ScrollRestoration />
        <Scripts />
      </body>
    </html>
  )
}

export function ErrorBoundary() {
  // The loader never throws, so its data is there whenever the root loader
  // ran; it is missing (and PostHog stays off) when the request matched no
  // route at all.
  const data = useRouteLoaderData<typeof loader>("root")
  return (
    <>
      <PosthogRuntime enabled={data?.posthogEnabled} />
      <ErrorBoundaryComponent />
    </>
  )
}

const Favicons = () => {
  return (
    <>
      <link rel="icon" type="image/svg+xml" href="/favicon.svg" />
      <link
        rel="apple-touch-icon-precomposed"
        sizes="57x57"
        href="/apple-touch-icon-57x57.png"
      />
      <link
        rel="apple-touch-icon-precomposed"
        sizes="114x114"
        href="/apple-touch-icon-114x114.png"
      />
      <link
        rel="apple-touch-icon-precomposed"
        sizes="72x72"
        href="/apple-touch-icon-72x72.png"
      />
      <link
        rel="apple-touch-icon-precomposed"
        sizes="144x144"
        href="/apple-touch-icon-144x144.png"
      />
      <link
        rel="apple-touch-icon-precomposed"
        sizes="60x60"
        href="/apple-touch-icon-60x60.png"
      />
      <link
        rel="apple-touch-icon-precomposed"
        sizes="120x120"
        href="/apple-touch-icon-120x120.png"
      />
      <link
        rel="apple-touch-icon-precomposed"
        sizes="76x76"
        href="/apple-touch-icon-76x76.png"
      />
      <link
        rel="apple-touch-icon-precomposed"
        sizes="152x152"
        href="/apple-touch-icon-152x152.png"
      />
      <link
        rel="icon"
        type="image/png"
        href="/favicon-196x196.png"
        sizes="196x196"
      />
      <link
        rel="icon"
        type="image/png"
        href="/favicon-96x96.png"
        sizes="96x96"
      />
      <link
        rel="icon"
        type="image/png"
        href="/favicon-32x32.png"
        sizes="32x32"
      />
      <link
        rel="icon"
        type="image/png"
        href="/favicon-16x16.png"
        sizes="16x16"
      />
      <link
        rel="icon"
        type="image/png"
        href="/favicon-128.png"
        sizes="128x128"
      />
    </>
  )
}
